import type { GetState, SetState } from "zustand";

import { MAX_COMPETENCY_FRAMEWORK_SIZE } from "@performyard/service-contracts";
import type { InterviewerHarnessType } from "@performyard/service-contracts";

import type {
	CompetencyEditDiff,
	CompetencyFrameworkState,
	CompetencySuggestion,
	EditableCompetencyFields,
} from "../../types";
import type { AskPerformYardState } from "../types";

import { setActiveInterviewerHarness } from "../helpers";

export type CompetencySlice = {
	competencyFramework: CompetencyFrameworkState | null;
	setCompetencySuggestions: (messageId: string, suggestions: CompetencySuggestion[], fromImport?: boolean) => void;
	updateEditedCompetencyFields: (id: string, fields: Partial<EditableCompetencyFields>) => void;
	setCompetencyFrameworkStatus: (status: CompetencyFrameworkState["status"], errorMessage?: string | null) => void;
	clearCompetencyFramework: () => void;
	removeCompetency: (id: string) => void;
	markCompetencyCreating: (id: string) => void;
	markCompetencyCreated: (id: string) => void;
	setCompetencyItemError: (id: string, message: string) => void;
	applyCompetencyEdit: (diff: CompetencyEditDiff, anchorMessageId?: string) => void;
	isCompetencyEditPanelOpen: boolean;
	openCompetencyEditPanel: () => void;
	closeCompetencyEditPanel: () => void;
	activeInterviewerHarness: InterviewerHarnessType | null;
	setActiveInterviewerHarness: (harness: InterviewerHarnessType | null) => void;
	deactivateInterviewerHarness: () => void;
};

export const createCompetencySlice = (set: SetState<AskPerformYardState>, get: GetState<AskPerformYardState>) => ({
	competencyFramework: null,
	isCompetencyEditPanelOpen: false,
	openCompetencyEditPanel: () => set({ isCompetencyEditPanelOpen: true }),
	closeCompetencyEditPanel: () => set({ isCompetencyEditPanelOpen: false }),
	activeInterviewerHarness: null,

	setActiveInterviewerHarness: (harness: InterviewerHarnessType | null) => {
		const leaving = get().activeInterviewerHarness;
		setActiveInterviewerHarness(harness);
		set({ activeInterviewerHarness: harness });
		if (leaving !== null && leaving !== harness) {
			get().clearCompetencyFramework();
			get().clearReportBuilder();
		}
	},

	deactivateInterviewerHarness: () => {
		setActiveInterviewerHarness(null);
		set({ activeInterviewerHarness: null });
	},
	setCompetencySuggestions: (messageId, suggestions, fromImport = false) => {
		set((state) => {
			const framework = state.competencyFramework;
			const isSameMessage = framework && framework.messageId === messageId;
			const hasUnsavedEdits = framework && Object.keys(framework.editedFields).length > 0;

			const shouldPreserve = isSameMessage && hasUnsavedEdits;

			return {
				messages: state.messages.map((message) =>
					message.id === messageId ? { ...message, competencySuggestions: suggestions } : message
				),
				...(shouldPreserve
					? {}
					: {
							competencyFramework: {
								suggestions,
								editedFields: {},
								status: "pending" as const,
								errorMessage: null,
								messageId,
								itemStatuses: {},
								itemErrors: {},
								fromImport,
							},
						}),
			};
		});
	},

	updateEditedCompetencyFields: (id, fields) => {
		set((state) => {
			if (!state.competencyFramework) return state;
			return {
				competencyFramework: {
					...state.competencyFramework,
					editedFields: {
						...state.competencyFramework.editedFields,
						[id]: { ...state.competencyFramework.editedFields[id], ...fields },
					},
				},
			};
		});
	},

	setCompetencyFrameworkStatus: (status, errorMessage: string | null = null) => {
		set((state) => {
			if (!state.competencyFramework) return state;
			return {
				competencyFramework: {
					...state.competencyFramework,
					status,
					errorMessage,
				},
			};
		});
	},
	removeCompetency: (id: string) => {
		set((state) => {
			if (!state.competencyFramework) return state;
			return {
				competencyFramework: {
					...state.competencyFramework,
					itemStatuses: { ...state.competencyFramework.itemStatuses, [id]: "removed" },
				},
			};
		});
	},

	markCompetencyCreating: (id: string) => {
		set((state) => {
			if (!state.competencyFramework) return state;
			return {
				competencyFramework: {
					...state.competencyFramework,
					itemStatuses: { ...state.competencyFramework.itemStatuses, [id]: "creating" },
				},
			};
		});
	},

	markCompetencyCreated: (id: string) => {
		set((state) => {
			if (!state.competencyFramework) return state;
			const { [id]: _removed, ...remainingErrors } = state.competencyFramework.itemErrors;
			return {
				competencyFramework: {
					...state.competencyFramework,
					itemStatuses: { ...state.competencyFramework.itemStatuses, [id]: "created" },
					itemErrors: remainingErrors,
				},
			};
		});
	},

	setCompetencyItemError: (id: string, message: string) => {
		set((state) => {
			if (!state.competencyFramework) return state;
			return {
				competencyFramework: {
					...state.competencyFramework,
					itemStatuses: { ...state.competencyFramework.itemStatuses, [id]: "pending" },
					itemErrors: { ...state.competencyFramework.itemErrors, [id]: message },
				},
			};
		});
	},
	applyCompetencyEdit: (diff: CompetencyEditDiff, anchorMessageId?: string) => {
		set((state) => {
			const framework = state.competencyFramework;
			if (!framework) {
				console.warn("[competency-edit] received an edit with no active framework; dropped");
				return state;
			}
			const suggestions = [...framework.suggestions];
			const itemStatuses = { ...framework.itemStatuses };
			const editedFields = { ...framework.editedFields };
			const isProtected = (id: string) => itemStatuses[id] === "created" || itemStatuses[id] === "removed";

			for (const update of diff.update) {
				const index = suggestions.findIndex((suggestion) => suggestion.id === update.id);
				if (index === -1) {
					console.warn("[competency-edit] update targets an unknown id; skipped", update.id);
					continue;
				}
				if (isProtected(update.id)) continue;
				suggestions[index] = { ...suggestions[index], ...update };
				delete editedFields[update.id];
			}
			for (const removal of diff.remove) {
				const index = suggestions.findIndex((suggestion) => suggestion.id === removal.id);
				if (index === -1) {
					console.warn("[competency-edit] remove targets an unknown id; skipped", removal.id);
					continue;
				}
				if (isProtected(removal.id)) continue;
				itemStatuses[removal.id] = "removed";
			}
			// Cap on live items only — removed tombstones stay in the array but shouldn't count.
			let liveCount = suggestions.filter((suggestion) => itemStatuses[suggestion.id] !== "removed").length;
			for (const addition of diff.add) {
				if (liveCount >= MAX_COMPETENCY_FRAMEWORK_SIZE) break;
				suggestions.push(addition);
				liveCount++;
			}

			// Re-anchor the card to the newest message so the edit is visible where the admin is reading.
			const targetMessageId = anchorMessageId ?? framework.messageId;
			return {
				competencyFramework: {
					...framework,
					suggestions,
					itemStatuses,
					editedFields,
					messageId: targetMessageId,
				},
				messages: state.messages.map((message) => {
					if (message.id === targetMessageId) return { ...message, competencySuggestions: suggestions };
					if (message.id === framework.messageId) return { ...message, competencySuggestions: undefined };
					return message;
				}),
			};
		});
	},
	clearCompetencyFramework: () => set({ competencyFramework: null }),
});
